import Vue from 'vue';
import AjaxUtils from '../../util/ajax_utils';
import { removeEmptyVals, objsAreEqual } from '../_ui/_utils/useObjectUtils';
import { ISO_3166_NAMES } from '../../util/iso3166_tools';
import {
    ApckTrustRisksColumn,
    ApckTrustCountryColumn
} from './trust_columns';

export const ApckTrustCountriesTable = {
    props: {
        src: {
            type: String,
            default: '/trust/countries/list',
        },
    },
    data: function () {
        return {
            loading: false,
            filters: {
                domain: null,
                country: null,
                date_from: null,
                date_to: null
            },
            availableFilters: {
                domains: []
            },
            suggestions: {
                countries: []
            },
            cols: [
                {
                    data: 'iso_3166',
                    heading: 'Country',
                    ctype: 'trust-country',
                    width: 200,
                },
                {
                    data: 'count',
                    heading: 'Third Parties',
                    width: 120,
                    center: true,
                },
                {
                    data: 'risks',
                    heading: 'Trusts',
                    ctype: 'trust-risks',
                    width: 160,
                },
                {
                    data: 'vulns',
                    heading: 'Vulnerabilities',
                    ctype: 'trust-risks',
                    width: 160,
                },
            ],
            actions: [
                {
                    title: 'View Third Parties',
                    icon: 'fa-eye',
                    class: 'btn-secondary',
                    handler: this.viewCountryDetails,
                    useOwnCol: true,
                    hideLabel: true,
                },
            ],
        }
    },
    computed: {
        tableSrc() {
            return this.src;
        },
        filterOptions() {
            return {
                domains: [
                    {
                        label: 'All Domains',
                        value: null,
                    },
                    ...this.availableFilters.domains.map((item) => ({
                        label: item.value,
                        value: item.value,
                    })),
                ],
            }
        },
        domainFilterLabel() {
            const selectedDomain = this.filters.domain;
            const matchingDomain = this.filterOptions.domains.find(domain => domain.value === selectedDomain);
            return matchingDomain ? matchingDomain.label : this.filterOptions.domains[0].label;
        },
        tableKey() {
            return `countries-table-${this._uid}`
        },
        hasFilters() {
            if(this.dropdownsChangedToAll()) return true
            return Object.keys(removeEmptyVals(this.filters)).length > 0
        },
        hasRouteQuery() {
            return Object.keys(this.$route.query).length > 0
        },
        queriesMatch() {
            const activeFilters = removeEmptyVals(this.filters)
            const currentQuery = removeEmptyVals(this.$route.query)

            return objsAreEqual(activeFilters, currentQuery)
        },
        disableClear() {
            return !this.hasFilters && !this.hasRouteQuery
        },
        disableSubmit() {
            return this.disableClear || this.queriesMatch
        }
    },
    methods: {
        viewCountryDetails: function (rec) {
            const countryCode = rec.get('iso_3166');
            // Navigate to relationships filtered by country
            window.location.href = `/trust/relationships?country=${countryCode}`;
        },
        setDomainFilter(value) {
            this.filters.domain = value || null
        },
        setCountryFilter(value) {
            this.filters.country = value ? value.toUpperCase() : null
        },
        fetchCountrySuggestions() {
            this.suggestions.countries = Object.entries(ISO_3166_NAMES)
                .map(([key, value]) => {
                    return {
                        label: `${key} - ${value}`,
                        flag: key,
                        value: key
                    }
                });
        },
        handleSubmit() {
            if(this.disableSubmit && this.hasRouteQuery) {
                this.clearFilters()
            } else {
                this.applyFilters()
            }
        },
        dropdownsChangedToAll() {
            return (!this.filters.domain && this.$route.query.domain)
        },
        clearFilters() {
            this.filters = {
                domain: null,
                country: null,
                date_from: null,
                date_to: null
            };
            this.$router.push({
                query: {}
            })

            this.applyFilters()
        },
        getFiltersFromUrl() {
            if(!this.hasRouteQuery) return;

            const originalFilters = this.filters
            const queryFilters = removeEmptyVals(this.$route.query);

            this.filters = { ...originalFilters, ...queryFilters};
        },
        updateUrlQuery() {
            if (this.hasFilters && !this.queriesMatch) {
                this.$router.push({
                    query: removeEmptyVals(this.filters)
                })
            }
        },
        applyFilters() {
            if (this.$refs.countriesTable) {

                this.updateUrlQuery()

                // Build query parameters from filters
                let params = {};
                Object.keys(this.filters).forEach(key => {
                    params[key] = this.filters[key];
                });

                // Update table with filters
                this.$refs.countriesTable.$data.store.load(params);
            }
        },
        async loadFilterOptions() {
            // Load available domains for filter dropdown
            const cmp = this;

            await AjaxUtils.ajax_get({
                url: '/trust/scope',
                success: (data) => {
                    if (data && data.success) {
                        cmp.availableFilters.domains = data.data || [];
                    }
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    show_success_flash: false,
                    flash_404_errors: false
                }
            });
        },
        tableRowAction(row) {
            this.viewCountryDetails(row);
        }
    },
    created: function () {
        // Register custom column components
        Vue.component('apck-trust-risks', ApckTrustRisksColumn);
        Vue.component('apck-trust-country', ApckTrustCountryColumn);
    },
    async mounted() {
        await this.loadFilterOptions();
        this.getFiltersFromUrl()
        this.applyFilters();
        this.fetchCountrySuggestions();
    },
    template: `
        <ui-page title="Trust Monitor Countries" stickyTitle>
            <template #content>
                <ui-layout direction="column" gap="4">
                    <!-- Search and Filter Options -->
                    <ui-layout direction="column" justify="start">
                        <ui-layout gap="2" align="center">
                            <ui-icon type="secondary" icon="fa-filter" />
                            <ui-heading tag="span" size="md">Filters</ui-heading>
                        </ui-layout>
                        
                        <ui-layout
                            tag="form"
                            gap="4"
                            align="end"
                            name="trust_country_filter"
                            @keydown.enter.native.prevent="handleSubmit"
                            @submit.native.prevent="handleSubmit"
                        >
                            <ui-input-text
                                label="Country"
                                placeholder="eg: United Kingdom"
                                type="text"
                                query="country"
                                :modelValue="filters.country"
                                :suggestions="suggestions.countries"
                                :suggestionLimit="1"
                                @update:modelValue="setCountryFilter"
                            />

                            <ui-dropdown-radio
                                name="domainFilter"
                                :modelValue="filters.domain"
                                :options="filterOptions.domains"
                                :label="domainFilterLabel"
                                align="left"
                                @update:modelValue="setDomainFilter"
                            />

                            <ui-button
                                label="Submit"
                                type="primary"
                                :disabled="disableSubmit"
                                nativeType="submit"
                            />

                            <ui-button
                                label="Clear"
                                type="primary"
                                :color="disableClear ? null : 'red'"
                                :disabled="disableClear"
                                @click.prevent="clearFilters"
                            />
                        </ui-layout>
                    </ui-layout>
                    
                    <!-- Countries Table -->
                    <apck-table
                        ref="countriesTable"
                        class="trust-countries-table"
                        :columns="cols"
                        :src="tableSrc"
                        :actions="actions"
                        :limit="50"
                        :border="false"
                        :autoload="false"
                        :key="tableKey"
                        :show-toolbar="false"
                        :sglRowAction="tableRowAction"
                        newStyleTable
                    />
                </ui-layout>
                
                <ui-spinner :show="loading" theme="dark" />
            </template>
        </ui-page>
    `
};
